import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const AUTH_URL = "https://auth.emergentagent.com";
const REDIRECT_URI = window.location.origin + "/auth/callback";

export default function LoginPage() {
  const { isAuthenticated, businessProfile, loading } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("google"); // google | email-login | email-register
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(businessProfile ? "/configure" : "/verify-business");
    }
  }, [isAuthenticated, businessProfile, loading, navigate]);

  const handleGoogleLogin = () => {
    window.location.href = `${AUTH_URL}/?redirect=${encodeURIComponent(REDIRECT_URI)}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error("Please fill in all fields"); return; }
    if (mode === "email-register" && !form.name) { toast.error("Please enter your name"); return; }

    setSubmitting(true);
    try {
      let result;
      if (mode === "email-register") {
        const res = await axios.post(`${API}/auth/register`, {
          name: form.name, email: form.email, password: form.password
        }, { withCredentials: true });
        result = res.data;
      } else {
        const res = await axios.post(`${API}/auth/login-email`, {
          email: form.email, password: form.password
        }, { withCredentials: true });
        result = res.data;
      }
      if (result.has_business_profile) {
        window.location.href = "/configure";
      } else {
        window.location.href = "/verify-business";
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-3 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-2 justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/6nsrlqnk_Gemini_Generated_Image_wzvx61wzvx61wzvx.png" 
            alt="OneIT" className="w-14 h-14 object-contain" 
          />
          <span className="font-heading font-bold text-2xl text-foreground">OneIT</span>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          <div className="text-center mb-6">
            <h1 className="font-heading text-2xl font-bold text-foreground" data-testid="login-title">
              {mode === "email-register" ? "Create Account" : "Welcome Back"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === "email-register" 
                ? "Sign up with your business email" 
                : "Sign in to access your IT dashboard"}
            </p>
          </div>

          {/* Mode tabs */}
          <div className="flex rounded-lg bg-secondary p-1 mb-6">
            <button
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                mode === "google" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
              }`}
              onClick={() => setMode("google")}
              data-testid="tab-google"
            >
              Google
            </button>
            <button
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                mode === "email-login" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
              }`}
              onClick={() => setMode("email-login")}
              data-testid="tab-email-login"
            >
              Email Login
            </button>
            <button
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                mode === "email-register" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
              }`}
              onClick={() => setMode("email-register")}
              data-testid="tab-email-register"
            >
              Register
            </button>
          </div>

          {/* Google Auth */}
          {mode === "google" && (
            <div className="space-y-4">
              <Button
                className="w-full bg-foreground text-background hover:bg-foreground/90 h-11"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Use your business email (personal emails like Gmail are not accepted)
              </p>
            </div>
          )}

          {/* Email forms */}
          {(mode === "email-login" || mode === "email-register") && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              {mode === "email-register" && (
                <div>
                  <Label htmlFor="name" className="text-foreground">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="mt-1 bg-background border-border"
                    data-testid="input-name"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-foreground">Business Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  className="mt-1 bg-background border-border"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-foreground">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 8 characters"
                  value={form.password}
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  className="mt-1 bg-background border-border"
                  data-testid="input-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#007AFF] text-white hover:bg-[#0055CC] h-11"
                disabled={submitting}
                data-testid="email-submit-btn"
              >
                {submitting ? "Please wait..." : mode === "email-register" ? "Create Account" : "Sign In"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Personal emails (Gmail, Yahoo, etc.) are not accepted.
              </p>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
